import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Flex, Heading, HStack, Icon, IconButton, Input, InputGroup, InputLeftElement, Select,
  Spinner, Table, TableContainer, Tbody, Text, Th, Thead, Tr, Tooltip, useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiBookOpen, FiChevronDown, FiChevronUp, FiExternalLink, FiMaximize2, FiSearch,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import GlassCard from '../GlassCard';
import SalesBibleRowEditor, { STATE_COLOR } from './SalesBibleRow';
import SalesBibleLifecycleModal from './SalesBibleLifecycleModal'; // item 1144
import AssignCustomerPmModal from './AssignCustomerPmModal'; // item 1172
import WorkOrderLeadsModal from '../WorkOrderLeadsModal'; // item 1172
import useSalesBible, { SalesBibleRow, paymentLabelOf, PAYMENT_RANK } from '../../hooks/useSalesBible';
import useSalesBibleActions from '../../hooks/useSalesBibleActions';
import useCompanyFiscal from '../../hooks/useCompanyFiscal';
import { fiscalYearOf, currentFiscalYear } from '../../models/companyFiscal';

interface SalesBibleCardProps {
  // item 273 — focus mode: fill the viewport height so as many rows as possible show,
  // and drop the "open focus mode" button (we're already in it).
  focus?: boolean;
}

// item 608 — the board's columns, in render order. `get` returns the sortable key for a
// clickable header; the FIRST column is the sticky job-name column. The row component
// (SalesBibleRow) renders its cells in exactly this order — keep the two in lockstep.
type SortVal = string | number;
interface ColumnDef {
  key: string;
  label: string;
  get: (r: SalesBibleRow) => SortVal;
}
const COLUMNS: ColumnDef[] = [
  { key: 'jobName', label: 'Job Name', get: (r) => r.jobName.toLowerCase() },
  // item 1587 (Chad: "the official project name the job is coming from")
  { key: 'project', label: 'Project', get: (r) => r.projectName.toLowerCase() },
  // item 607 — "DEAD / ALIVE" → "STATUS"; sorts by the lifecycle bucket
  { key: 'status', label: 'Status', get: (r) => r.bucket },
  // item 1258 (Chad: "Luxor is 100% complete, they just owe the invoice") — collection state
  // decoupled from job status; sorts owes-first (biggest debt on top within owes)
  // item 1261 — sorts by the worded journey (Progress → Open → Deposited → Paid in Full),
  // biggest debt first within a rank
  {
    key: 'payment',
    label: 'Payment',
    get: (r) => PAYMENT_RANK[paymentLabelOf(r)] * 10_000_000 - r.owedTotal,
  },
  { key: 'integrator', label: 'Integrator', get: (r) => r.integrator.toLowerCase() },
  // item 609 — the job code (WO#), to the LEFT of Sales Person
  { key: 'jobCode', label: 'Job Code (WO#)', get: (r) => r.workOrderNumber },
  // item 612 — the customer's staff / PM contact, to the LEFT of Sales Person
  { key: 'customerStaffPm', label: 'Customer Staff / PM', get: (r) => r.customerStaffPm.toLowerCase() },
  { key: 'salesPerson', label: 'Sales Person', get: (r) => r.salesPerson.toLowerCase() },
  { key: 'projectLead', label: 'Project Lead', get: (r) => r.projectLead.toLowerCase() },
  // item 606 — sort by how many earners are assigned
  { key: 'commissions', label: 'Commissions', get: (r) => r.commissionEarners.reduce((n, g) => n + g.names.length, 0) },
  { key: 'illustrations', label: 'Illustrations', get: (r) => r.illustrationsComplete },
  { key: 'platform', label: 'Platform', get: (r) => r.platform.toLowerCase() },
  { key: 'contracted', label: 'Contracted', get: (r) => (r.contracted ? 1 : 0) },
  { key: 'depositBilled', label: 'Deposit Billed', get: (r) => (r.depositBilled ? 1 : 0) },
  { key: 'finalBilled', label: 'Final Billed', get: (r) => (r.finalBilled ? 1 : 0) },
  // item 611 — the customer's real PO number (captured in the guided approval)
  { key: 'po', label: 'PO #', get: (r) => r.poNumber },
  { key: 'deploymentDate', label: 'Deployment Date', get: (r) => r.deploymentDate },
  { key: 'notes', label: 'Notes', get: (r) => r.notes.toLowerCase() },
];

type SortDir = 'asc' | 'desc';

const compareVals = (a: SortVal, b: SortVal): number => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
};

// item 177 — the Sales Bible: an auto-populated job tracker on the dashboard (all roles).
// Derived columns (status, integrator, billing, WO#, commissions) update straight from the
// data; the manual columns (Platform, Deployment Date, Illustrations, Notes) are editable
// inline and saved per job (v2, proposal rows only). item 273 — also the full-page focus view.
const SalesBibleCard: React.FC<SalesBibleCardProps> = ({ focus }) => {
  const history = useHistory();
  const { rows, isLoading, isError } = useSalesBible();
  const { save } = useSalesBibleActions();
  const [q, setQ] = useState('');
  // item 1144 — the job whose lifecycle popup is open
  const queryClient = useQueryClient(); // item 1172 — refresh the bible after a leads save
  const [lifecycleRow, setLifecycleRow] = useState<SalesBibleRow | null>(null);
  // item 1172 — the Assign→ targets: staff/PM picker + the WO leads editor, opened in place
  const [pmRow, setPmRow] = useState<SalesBibleRow | null>(null);
  const [leadsRow, setLeadsRow] = useState<SalesBibleRow | null>(null);
  // item 284/623 — filter the board by state (In Progress / Pending / Completed / Declined / Voided)
  const [stateFilter, setStateFilter] = useState('all');
  // item 608 — click-to-sort a column; null = the default group order (bucket, then newest)
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // item 285 — fiscal-year scope. null = follow the current FY until the user picks one.
  const { fiscal } = useCompanyFiscal();
  const ysm = fiscal?.yearStartMonth;
  const currentFy = currentFiscalYear(ysm);
  const [year, setYear] = useState<number | null>(null);
  const selectedYear = year ?? currentFy;
  const subColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const headBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const stickyBg = useColorModeValue('gray.50', 'gray.800');
  const phoneCardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const isPhone = useBreakpointValue({ base: true, md: false }) ?? false; // item 1089

  // the fiscal years to offer: from the earliest job's FY up to the current FY (desc)
  const years = useMemo(() => {
    const fys = new Set<number>([currentFy]);
    rows.forEach((r) => {
      const s = fiscalYearOf(r.date, ysm);
      if (s != null) fys.add(s);
      const c = fiscalYearOf(r.closedDate, ysm);
      if (c != null) fys.add(c);
    });
    const min = Math.min(...Array.from(fys));
    const list: number[] = [];
    for (let y = currentFy; y >= min; y -= 1) list.push(y);
    return list;
  }, [rows, ysm, currentFy]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (t && !`${r.jobName} ${r.projectName} ${r.integrator} ${r.salesPerson} ${r.customerStaffPm} ${r.workOrderNumber} ${r.proposalNumber} ${r.poNumber}`
        .toLowerCase().includes(t)) return false;
      // item 623 — state filter (clean states: In Progress / Pending / Completed / Declined / Voided)
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      // item 285 — fiscal-year rollover: OPEN jobs (active work + open opportunities) carry
      // FORWARD (visible from their start FY onward); closed jobs (Completed / Declined / Voided)
      // stay in the FY they closed.
      const isOpen = r.state === 'In Progress' || r.state === 'Pending';
      if (isOpen) {
        const sf = fiscalYearOf(r.date, ysm);
        if (sf != null && sf > selectedYear) return false;
      } else {
        const cf = fiscalYearOf(r.closedDate, ysm);
        if (cf !== selectedYear) return false;
      }
      return true;
    });
  }, [rows, q, stateFilter, selectedYear, ysm]);

  // item 623 — apply the chosen column sort, else the default top→bottom hierarchy:
  // In Progress → Pending → Completed → Declined → Voided (bucket), newest first within a bucket.
  const sorted = useMemo(() => {
    const list = filtered.slice();
    const col = sortKey ? COLUMNS.find((c) => c.key === sortKey) : null;
    if (!col) {
      return list.sort((a, b) => a.bucket - b.bucket || b.date.localeCompare(a.date));
    }
    return list.sort((a, b) => {
      const r = compareVals(col.get(a), col.get(b));
      const signed = sortDir === 'asc' ? r : -r;
      // stable tiebreak so equal cells keep the default grouping
      return signed || a.bucket - b.bucket || b.date.localeCompare(a.date);
    });
  }, [filtered, sortKey, sortDir]);

  // item 608 — header click cycles: asc → desc → back to the default group order
  const onSort = (key: string) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };

  // item 304 — the pinned first column needs an OPAQUE bg + a z-index above the scrolling
  // cells + clipped content, or columns bleed/overlap on horizontal scroll.
  const stickyCell = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 2,
    bg: stickyBg,
    minW: '220px',
    maxW: '320px',
    overflow: 'hidden',
  };

  return (
    <GlassCard p={{ base: 4, md: 6 }}>
      {/* item 1159a (Chad: "our two large modals") — in FOCUS mode the page band already
          says SALES BIBLE; the duplicate title + blurb here just ate a header's worth of
          rows. Focus keeps only the count + controls. */}
      <HStack justify="space-between" align="flex-start" mb={1} wrap="wrap" sx={{ gap: '8px' }}>
        {focus ? (
          <HStack spacing={2}>
            <Badge variant="subtle">{`${sorted.length} jobs`}</Badge>
          </HStack>
        ) : (
          <Box>
            <HStack spacing={2} mb={1}>
              <Icon as={FiBookOpen} />
              <Heading size="md">Sales Bible</Heading>
              <Badge variant="subtle">{sorted.length}</Badge>
            </HStack>
            <Text fontSize="sm" color={subColor}>
              Every job for the selected fiscal year — proposals &amp; standalone work orders. Open opportunities &amp; jobs carry forward; declined &amp; completed stay in the year they closed. Status, billing &amp; commission assignments update automatically; Platform, Deployment Date, Illustrations &amp; Notes are yours to edit. Click a column header to sort.
            </Text>
          </Box>
        )}
        <HStack spacing={2} flexWrap="wrap" sx={{ rowGap: '6px' }}>
          {/* item 285 — fiscal-year switcher (open jobs carry forward; closed stay put) */}
          <Select size="sm" maxW="120px" value={selectedYear} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{`FY ${y}`}</option>)}
          </Select>
          {/* item 284 — filter the board by state */}
          <Select size="sm" maxW="170px" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="all">All states</option>
            <option value="In Progress">In Progress</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
            <option value="Declined">Declined</option>
            <option value="Voided">Voided</option>
          </Select>
          <InputGroup size="sm" maxW="240px">
            <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color={subColor} /></InputLeftElement>
            <Input placeholder="Search jobs…" value={q} onChange={(e) => setQ(e.target.value)} />
          </InputGroup>
          {!focus && (
            <>
              <Tooltip label="Focus mode (full page)" hasArrow>
                <IconButton
                  aria-label="Open Sales Bible focus mode"
                  icon={<FiMaximize2 />}
                  size="sm"
                  variant="ghost"
                  onClick={() => history.push('/sales-bible')}
                />
              </Tooltip>
              {/* item 421 — undock into its own window (chat's Undock pattern) */}
              <Tooltip label="Undock into its own window" hasArrow>
                <IconButton
                  aria-label="Undock Sales Bible"
                  icon={<FiExternalLink />}
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open('/sales-bible', 'basixSalesBible', 'popup=yes,width=1280,height=860')}
                />
              </Tooltip>
            </>
          )}
        </HStack>
      </HStack>

      {isLoading && <Spinner mt={4} />}
      {isError && <Text mt={4} color="red.400" fontSize="sm">Couldn&apos;t load the Sales Bible. Reload to try again.</Text>}
      {!isLoading && !isError && sorted.length === 0 && (
        <Text mt={4} color={subColor}>No jobs yet — they appear here as proposals and work orders are created.</Text>
      )}

      {!isLoading && !isError && sorted.length > 0 && (
        <>
          {/* item 1089 (Chad: "not sure the sales bible can be optimized more — take a peek")
            — on phones the board becomes a compact JOB CARD list: name + state, the people,
            the numbers, billing flags. Read-only by design (the bar: read anything, act on
            anything simple, author on desktop); editing stays on the desktop board. */}
          {isPhone && (
          <Box mt={3}>
            {sorted.map((r: SalesBibleRow) => (
              <Box key={`m-${r.kind}-${r.id}`} borderWidth="1px" borderColor={phoneCardBorder} borderRadius="lg" p={3} mb={2}>
                <Flex align="flex-start" sx={{ gap: '8px' }}>
                  <Text fontWeight="semibold" fontSize="sm" flex="1" minW={0}>{r.jobName}</Text>
                  <Badge colorScheme={STATE_COLOR[r.state]} variant="solid" flexShrink={0}>{r.state}</Badge>
                </Flex>
                <Text fontSize="xs" color={subColor} mt={1} noOfLines={1}>
                  {[r.workOrderNumber || r.proposalNumber, r.integrator, r.salesPerson].filter(Boolean).join(' · ')}
                </Text>
                <HStack mt={1.5} spacing={2} flexWrap="wrap" sx={{ rowGap: '4px' }}>
                  {r.contracted && <Badge variant="subtle" colorScheme="purple" fontSize="0.6rem">CONTRACTED</Badge>}
                  {r.depositBilled && <Badge variant="subtle" colorScheme="green" fontSize="0.6rem">DEPOSIT BILLED</Badge>}
                  {r.finalBilled && <Badge variant="subtle" colorScheme="green" fontSize="0.6rem">FINAL BILLED</Badge>}
                  {r.writtenOffTotal > 0 && <Badge variant="subtle" colorScheme="red" fontSize="0.6rem">WRITTEN OFF</Badge>}
                  {r.platform && <Badge variant="subtle" fontSize="0.6rem">{r.platform}</Badge>}
                </HStack>
              </Box>
            ))}
            {sorted.length === 0 && (
              <Text fontSize="sm" color={subColor} py={4} textAlign="center">No jobs in this view.</Text>
            )}
          </Box>
          )}
          {/* item 1214 (Chad: "the page should always be as big as the resolution allows and
              the ledger scrolls within those confines") — the LEDGER owns the vertical
              scroll in EVERY host (was focus-only): one scrollbar, sticky header, so you
              ride from In Progress to Complete/Void and back without moving the page. */}
          {!isPhone && (
          <TableContainer
            mt={3}
            overflowX="auto"
            overflowY="auto"
            maxH={focus ? 'calc(100vh - 220px)' : 'calc(100vh - 300px)'}
          >
            <Table size="sm" variant="simple">
              <Thead>
                {/* item 228(a): header cells nowrap so multi-word labels never wrap/collide on a
                  narrow card — the TableContainer scrolls horizontally instead.
                  item 608: every header is a click-to-sort control.
                  item 1214: the header row is STICKY inside the ledger's own scroll (opaque
                  bg so rows never bleed through). */}
                <Tr sx={{
                  '& > th': {
                    whiteSpace: 'nowrap', position: 'sticky', top: 0, bg: stickyBg, zIndex: 3,
                  },
                }}
                >
                  {COLUMNS.map((col, i) => {
                    const active = sortKey === col.key;
                    return (
                      <Th
                        key={col.key}
                      // item 304 — the first column is pinned; opaque bg + higher z so the
                      // scrolling columns don't bleed through it. item 1214 — the corner cell
                      // is sticky BOTH ways (left + top), so it outranks header and column.
                        sx={i === 0 ? { ...stickyCell, top: 0, zIndex: 4 } : undefined}
                        cursor="pointer"
                        userSelect="none"
                        onClick={() => onSort(col.key)}
                        title={`Sort by ${col.label}`}
                      >
                        <HStack spacing={1} display="inline-flex" align="center">
                          <Box as="span">{col.label}</Box>
                          {active && (
                          <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} boxSize={3} />
                          )}
                        </HStack>
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {sorted.map((r: SalesBibleRow) => (
                  <SalesBibleRowEditor key={`${r.kind}-${r.id}`} row={r} stickyCell={stickyCell} onSave={save} onLifecycle={setLifecycleRow} onAssignPm={setPmRow} onAssignLead={setLeadsRow} />
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          )}
        </>
      )}
      {/* item 1144 — the full rules report */}
      <SalesBibleLifecycleModal row={lifecycleRow} onClose={() => setLifecycleRow(null)} />
      <AssignCustomerPmModal row={pmRow} onClose={() => setPmRow(null)} />
      {leadsRow && (
        <WorkOrderLeadsModal
          workOrderId={leadsRow.workOrderId ?? leadsRow.id}
          workOrderNumber={leadsRow.workOrderNumber}
          isOpen
          onClose={() => {
            setLeadsRow(null);
            // the bible's PROJECT LEAD reads the proposals/WO indexes — refresh both so the
            // Assign→ turns into the name without a manual reload (the modal itself doesn't)
            queryClient.invalidateQueries('proposals');
            queryClient.invalidateQueries('workOrders');
          }}
        />
      )}
    </GlassCard>
  );
};

SalesBibleCard.defaultProps = {
  focus: false,
};

export default SalesBibleCard;
